import type { ServiceState, WorkstationServiceStatus } from '@visionqc/shared';

/**
 * One glanceable answer to "is this workstation's verification running?".
 *
 * The service has more states than on/off — a workstation can be switched on yet
 * not actually verifying because it has no camera, no published procedure, or
 * the background monitor is not live. This pill names the real state and colours
 * it by whether action is needed: green = running, amber = switched on but
 * blocked, grey = off.
 */

const META: Record<ServiceState, { label: string; tone: 'on' | 'warn' | 'off'; hint: string }> = {
  off: { label: 'Off', tone: 'off', hint: 'Not activated — switch it on to start verifying.' },
  no_camera: { label: 'No camera', tone: 'warn', hint: 'Activated, but no camera is attached to this workstation.' },
  no_procedure: {
    label: 'No SOP',
    tone: 'warn',
    hint: 'Activated, but there is no published SOP to verify against.',
  },
  monitor_off: {
    label: 'Monitor off',
    tone: 'warn',
    hint: 'Ready, but the background monitor process is not running on the server.',
  },
  starting: { label: 'Starting…', tone: 'warn', hint: 'Ready — the monitor will pick this workstation up shortly.' },
  idle: { label: 'Ready', tone: 'on', hint: 'Running and waiting for operator activity to begin a cycle.' },
  running: { label: 'Running', tone: 'on', hint: 'A verification cycle is in progress right now.' },
};

const TONE_COLOR: Record<'on' | 'warn' | 'off', string> = {
  on: '#2e7d32',
  warn: 'var(--warn, #b7791f)',
  off: 'var(--muted, #888)',
};

export function ServiceStatusPill({ status }: { status: WorkstationServiceStatus | null }) {
  const state: ServiceState = status?.state ?? 'off';
  const m = META[state];
  const color = TONE_COLOR[m.tone];
  return (
    <span
      title={m.hint}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        fontWeight: 600,
        padding: '2px 10px',
        borderRadius: 999,
        border: `1px solid ${color}`,
        color,
        background: 'transparent',
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: color,
          boxShadow: m.tone === 'on' ? `0 0 0 3px ${color}22` : 'none',
        }}
      />
      {m.label}
    </span>
  );
}
